import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const SEEN_KEY = "be_splash_seen";
const NAVY = "#061C31"; // sampled from the master logo's own background

const seenBefore = () => {
    try {
        return !!sessionStorage.getItem(SEEN_KEY);
    } catch {
        return false;
    }
};

const Splash = () => {
    const [phase, setPhase] = useState(() =>
        seenBefore() ? "done" : "enter"
    );
    const [ready, setReady] = useState(false);

    const finish = () => {
        try {
            sessionStorage.setItem(SEEN_KEY, "1");
        } catch {}
        setPhase("done");
    };

    useEffect(() => {
        if (phase === "done") return undefined;
        const reduced = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;
        const t = reduced
            ? { ready: 400, exit: 900, end: 1300 }
            : { ready: 1750, exit: 2200, end: 3050 };
        const timers = [
            setTimeout(() => setReady(true), t.ready),
            setTimeout(() => setPhase("exit"), t.exit),
            setTimeout(finish, t.end),
        ];
        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (phase === "done") return null;

    const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
    ).matches;
    const exiting = phase === "exit";

    return (
        <motion.div
            data-testid="splash-screen"
            aria-hidden="true"
            initial={{ opacity: 1 }}
            animate={{ opacity: exiting ? 0 : 1 }}
            transition={{ duration: reduced ? 0.3 : 0.85, ease: "easeInOut" }}
            className="pointer-events-none fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#061C31]"
        >
            <div className="blueprint-bg blueprint-fade pointer-events-none absolute inset-0 opacity-60" />

            <motion.img
                src="/brand/buildease-master-square.jpg"
                alt="BuildEase — Simplify. Build. Live."
                initial={{ opacity: reduced ? 1 : 0, y: reduced ? 0 : 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                    duration: reduced ? 0.4 : 0.95,
                    ease: [0.22, 1, 0.36, 1],
                    delay: reduced ? 0.1 : 0.4,
                }}
                className="relative z-10 w-[min(500px,80vw)] object-contain"
            />

            <motion.div
                initial={{ opacity: reduced ? 1 : 0, scaleX: reduced ? 1 : 0 }}
                animate={{ opacity: 0.9, scaleX: 1 }}
                transition={{
                    duration: reduced ? 0.3 : 1.6,
                    ease: "easeInOut",
                    delay: reduced ? 0.2 : 0.5,
                }}
                className="relative z-10 mt-12 h-px w-44 origin-left bg-accent"
                data-testid="splash-progress-line"
            />

            <motion.p
                initial={{ opacity: reduced ? 1 : 0 }}
                animate={{ opacity: 0.55 }}
                transition={{ duration: 0.7, delay: reduced ? 0.2 : 0.95 }}
                className="relative z-10 mt-5 font-mono text-[13px] tracking-[0.3em] text-mist"
                data-testid="splash-label"
            >
                {ready ? "SYSTEM / READY" : "BUILD INTELLIGENCE / INITIALISING"}
            </motion.p>

            <button
                type="button"
                onClick={finish}
                data-testid="splash-skip"
                className="sr-only"
                aria-label="Skip intro"
            >
                Skip intro
            </button>
        </motion.div>
    );
};

export default Splash;
